import {
  EventActivityParticipantEntry,
  EventActivityParticipantSelection,
} from './eventActivityParticipantEntry'

type ResolveParticipantEntryInput = {
  participant: wowaudit.EventParticipant
  activityIds: number[]
  teamMembers: wowaudit.TeamMembership[]
  rosterCharacterById?: Record<number, wowaudit.CharacterReference>
}

type ResolveParticipantEntriesInput = {
  participants: wowaudit.EventParticipant[]
  activityIds: number[]
  teamMembers: wowaudit.TeamMembership[]
}

const resolveSelectionCharacter = (
  participant: wowaudit.EventParticipant,
  availableTeamMemberships: wowaudit.TeamMembership[],
  rosterCharacterById: Record<number, wowaudit.CharacterReference> | undefined,
  characterId: number,
) => {
  if (
    participant.altCharacterId != null &&
    participant.altCharacterId === characterId &&
    participant.altCharacterReference
  ) {
    return participant.altCharacterReference
  }

  if (participant.characterId === characterId) {
    return participant.characterReference
  }

  return (
    availableTeamMemberships
      .map((teamMembership) => teamMembership.characterReference)
      .find((character) => character.id === characterId) ||
    rosterCharacterById?.[characterId] ||
    participant.characterReference
  )
}

export const resolveEventParticipantEntry = ({
  participant,
  activityIds,
  teamMembers,
  rosterCharacterById,
}: ResolveParticipantEntryInput): EventActivityParticipantEntry => {
  const member = participant.teamMembership
  const role = participant.role || member?.role || 'melee'
  const specId = participant.specId ?? member?.specId ?? null
  const participantUserId = participant.characterReference.userId
  const availableTeamMemberships = participantUserId
    ? teamMembers.filter(
        (teamMembership) =>
          teamMembership.characterReference.userId === participantUserId,
      )
    : []
  const characterId =
    participant.altCharacterId ??
    participant.characterId ??
    participant.characterReference.id
  const selections: Record<number, EventActivityParticipantSelection> = {}

  activityIds.forEach((activityId) => {
    const override = participant.selections[activityId.toString()]
    selections[activityId] = {
      selected:
        override?.selected ?? participant.effectiveDefaultSelectionStatus,
      role: override?.role ?? role,
      specId: override?.spec_id ?? specId,
      characterId: override?.character_id ?? characterId,
    }
  })
  const selected =
    activityIds.length > 0 &&
    activityIds.every((activityId) => selections[activityId].selected)

  return {
    participant,
    selected,
    role,
    specId,
    characterId,
    availableTeamMemberships,
    rosterCharacterById,
    selections,
  }
}

export const resolveEventParticipantEntries = ({
  participants,
  activityIds,
  teamMembers,
}: ResolveParticipantEntriesInput): EventActivityParticipantEntry[] => {
  const rosterCharacterById = teamMembers.reduce<
    Record<number, wowaudit.CharacterReference>
  >((acc, teamMembership) => {
    acc[teamMembership.characterReference.id] =
      teamMembership.characterReference
    return acc
  }, {})
  return participants.map((participant) =>
    resolveEventParticipantEntry({
      participant,
      activityIds,
      teamMembers,
      rosterCharacterById,
    }),
  )
}

export const resolveEventParticipantCharacter = (
  entry: EventActivityParticipantEntry,
  activityId?: number,
) => {
  const selectedCharacterId =
    activityId && entry.selections[activityId]
      ? entry.selections[activityId].characterId
      : entry.characterId
  return resolveSelectionCharacter(
    entry.participant,
    entry.availableTeamMemberships,
    entry.rosterCharacterById,
    selectedCharacterId,
  )
}
